import { Icon } from '@capsico/ui';
import type { CountryCode, OutletCard } from '@capsico/contracts';
import { costForTwo } from '../utils/money';
import { distanceText, etaText, ratingText } from '../utils/format';
import { VegMark } from './ui';

/**
 * C1 restaurant card. Full width: 16:9 cover (or a brand tint with the initial), Local Legend /
 * New badge on the image, name 15/700, rating chip, cuisines line, "₹300 for two · 25–30 min ·
 * 2.1 km". Closed outlets are greyed with the reason. Compact = 260px rail card.
 */
export function RestaurantCard({
  outlet,
  country,
  labels,
  compact = false,
  onOpen,
}: {
  outlet: OutletCard;
  country: CountryCode | null;
  labels: {
    forTwo: (amount: string) => string;
    legend: string;
    isNew: string;
    closed: string;
    has: (dishes: string) => string;
  };
  compact?: boolean;
  onOpen: () => void;
}) {
  const rating = ratingText(outlet.rating);
  const forTwo = costForTwo(country, outlet.costForTwoMinor);
  const meta = [
    forTwo ? labels.forTwo(forTwo) : null,
    etaText(outlet.eta),
    distanceText(outlet.distanceKm),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`block overflow-hidden rounded-2xl bg-surface text-left shadow-[inset_0_0_0_1px_var(--color-hair)] active:scale-[0.99] ${
        compact ? 'w-64 shrink-0' : 'w-full'
      } ${outlet.open ? '' : 'opacity-60 grayscale'}`}
    >
      <div className="relative aspect-video w-full bg-brand-soft">
        {outlet.coverUrl ? (
          <img src={outlet.coverUrl} alt="" loading="lazy" className="size-full object-cover" />
        ) : (
          <span className="flex size-full items-center justify-center text-4xl font-bold text-brand-ink">
            {outlet.name.slice(0, 1)}
          </span>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          {outlet.localLegend && (
            <span className="rounded-md bg-brand px-2 py-0.5 text-caption font-semibold text-on-brand">
              {labels.legend}
            </span>
          )}
          {outlet.isNew && (
            <span className="rounded-md bg-surface px-2 py-0.5 text-caption font-semibold text-brand-ink">
              {labels.isNew}
            </span>
          )}
        </div>
        {!outlet.open && (
          <span className="absolute inset-x-0 bottom-0 bg-scrim px-3 py-1.5 text-xs font-semibold text-white">
            {outlet.closedReason ?? labels.closed}
          </span>
        )}
      </div>
      <div className="px-3 pt-2.5 pb-3">
        <div className="flex items-start gap-2">
          <h3 className="min-w-0 flex-1 truncate text-title font-bold text-ink">{outlet.name}</h3>
          {rating && (
            <span className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-positive px-1.5 py-0.5 text-xs font-semibold text-white tabular">
              {rating}
              <Icon name="star" size={11} />
            </span>
          )}
        </div>
        <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-ink-2">
          {outlet.foodType === 'veg' && <VegMark type="veg" size={12} />}
          <span className="truncate">{outlet.cuisines.join(', ')}</span>
        </p>
        <p className="mt-0.5 truncate text-xs text-ink-3 tabular">{meta}</p>
        {outlet.matchedDishes.length > 0 && (
          <p className="mt-1.5 truncate text-xs font-medium text-brand-ink">
            {labels.has(
              outlet.matchedDishes
                .map((dish) => dish.name)
                .slice(0, 3)
                .join(', '),
            )}
          </p>
        )}
      </div>
    </button>
  );
}
